<template>
  <ContainerScrolling class="SearchDashboard" @show-more="onShowMore">
    <LayoutFill :no-gutters="solidHeader" :no-padding="solidHeader">
      <MainBanner :title="title" :solid-header="solidHeader" />
    </LayoutFill>
    <LayoutGutters>
      <v-row mt-4 :column="displayIsMobile">
        <v-col cols="12" sm="4" md="3">
          <h2 v-if="displayIsTablet || displayIsDesktop" class="d-sr-only">
            Refine Search
          </h2>
          <v-div
            :pl-3="displayIsMobile"
            :pr-3="displayIsMobile"
            :font-weight-bold="searchInputBold"
          >
            <v-text-field
              :color="searchInputColor"
              :value="searchRequest.text"
              aria-label="search term"
              @input="onUpdateSearchText"
              @keyup.enter.prevent="onSearch"
            >
              <template #append>
                <v-div layer-10>
                  <v-btn
                    rounded
                    icon
                    aria-label="submit search"
                    color="softened"
                    @click="onSearch"
                  >
                    <v-icon color="hardened">{{ mdiMagnify }}</v-icon>
                  </v-btn>
                </v-div>
              </template>
            </v-text-field>
          </v-div>
          <v-div
            v-if="displayIsMobile"
            title
            font-weight-bold
            ma-2
            pa-2
            pl-3
            foreground-primary
            border-all-primary-thin
            rounded
          >
            <h2 style="font-size: 1.25rem; display: inline">
              <button
                :aria-expanded="`${showDetails}`"
                style="width: 100%; text-align: left"
                @click.prevent="onToggleDisplay"
              >
                Refine Search
                <v-icon
                  color="searchInputColor"
                  class="mr-1 mt-1 float-right"
                  >{{ showDetails ? mdiChevronUp : mdiChevronDown }}</v-icon
                >
              </button>
            </h2>
          </v-div>

          <APSAEducateSearch
            v-if="showApsaEducateSearchLink"
            :search-text="searchResult.text"
            class="mb-4 mt-6 mt-md-0"
          />

          <SearchFacets
            v-if="showRefine"
            :subject-buckets="searchResult.subjectBuckets"
            :category-buckets="searchResult.categoryBuckets"
            :partner-buckets="searchResult.partnerBuckets"
            :content-buckets="searchResult.contentBuckets"
            :event-buckets="searchResult.eventBuckets"
            :published-date-buckets="searchResult.publishedDateBuckets"
            :selected-subjects="searchRequest.subjects"
            :selected-categories="searchRequest.categories"
            :selected-partners="searchRequest.partners"
            :selected-contents="searchRequest.contents"
            :selected-authors="searchRequest.authors"
            :selected-keywords="searchRequest.keywords"
            :selected-events="searchRequest.events"
            :selected-published-dates="searchRequest.publishedDates"
            :show-subjects="showSubjectFacets"
            :show-events="searchDashboardShowEventFacets"
            :show-categories="showCategoryFacets"
            :show-partners="showPartnerFacets"
            :show-published-dates="showPublishedDateFacets"
            :show-content-type-facets="showContentTypeSearch"
            @contentChanged="handleContentChanged"
            @subjectChanged="handleSubjectChanged"
            @eventChanged="handleEventChanged"
            @categoryChanged="handleCategoryChanged"
            @publishedDateChanged="handlePublishedDateChanged"
            @partnerChanged="handlePartnerChanged"
            @authorsChanged="handleAuthorsChanged"
            @keywordsChanged="handleKeywordsChanged"
          />
        </v-col>
        <v-col cols="12" sm="8" md="9">
          <transition name="fade-transition">
            <ResultsList
              :show-results="gettingFirst === false"
              :total-count="searchResult.totalCount"
              :search-term="searchResult.text"
              :results="searchResult.results"
              :facets="selectedFacetNames"
              :sort-options="sortOptions"
              :default-sort="selectedSort"
              :sort-is-changed="sortChanged"
              @sortChanged="handleSortChanged"
            />
          </transition>
          <transition name="fade-transition">
            <v-div v-if="gettingMore" text-center>
              <v-progress-circular
                indeterminate
                color="primary"
                aria-label="Loading content"
              />
            </v-div>
          </transition>
        </v-col>
      </v-row>
    </LayoutGutters>
  </ContainerScrolling>
</template>

<script>
import { mapState } from "vuex";
import { mdiMagnify, mdiChevronUp, mdiChevronDown } from "@mdi/js";
import mapFlags from "~/plugins/utilities/mapFlags";

import {
  displayIsDesktop,
  displayIsMobile,
  displayIsTablet,
} from "~/components/utilities/displayBreakpoints";

import ContainerScrolling from "~/components/layout/ContainerScrolling.vue";
import LayoutFill from "~/components/layout/LayoutFill.vue";
import LayoutGutters from "~/components/layout/LayoutGutters";

import MainBanner from "~/components/banner/MainBanner.vue";
import SearchFacets from "~/components/matches/SearchFacets.vue";
import ResultsList from "~/components/matches/ResultsList.vue";

import getPageTitle from "~/plugins/utilities/getPageTitle.js";
import getSiteName from "~/plugins/utilities/getSiteName.js";
import toCanonicalUrl from "~/plugins/utilities/toCanonicalUrl.js";

import APSAEducateSearch from "~/components/search/APSAEducateSearch.vue";

export default {
  name: "SearchDashboard",
  components: {
    ContainerScrolling,
    LayoutFill,
    LayoutGutters,
    MainBanner,
    SearchFacets,
    ResultsList,
    APSAEducateSearch,
  },
  data() {
    return {
      gettingFirst: false,
      gettingMore: false,
      showDetails: false,
      // icons
      mdiMagnify,
      mdiChevronUp,
      mdiChevronDown,
      sortOptions: [
        { key: "RELEVANT_DESC", name: "Relevance" },
        { key: "TITLE_ASC", name: "Title" },
        { key: "PUBLISHED_DATE_DESC", name: "Date Posted" },
      ],
      selectedSort: "RELEVANT_DESC",
      sortChanged: false,
    };
  },
  async fetch(context) {
    const { store, query, req, res } = context;

    Object.keys(query).forEach((key) => {
      if (query[key] === null) {
        query[key] = undefined;
      }
    });

    const params = { ...query };

    if (params.skip) {
      params.skip = parseInt(params.skip);
    }

    if (params.limit) {
      params.limit = parseInt(params.limit);
    }

    if (params?.subjects?.length > 0) {
      params.subjects = params.subjects.split(",");
    }

    if (params?.categories?.length > 0) {
      params.categories = params.categories.split(",");
    }

    if (params?.events?.length > 0) {
      params.events = params.events.split(",");
    }

    if (params?.publishedDates?.length > 0) {
      params.publishedDates = params.publishedDates.split(",");
    }

    if (params?.partners?.length > 0) {
      params.partners = params.partners.split(",");
    }

    if (params?.contents?.length > 0) {
      params.contents = params.contents.split(",");
    }

    if (params.authors) {
      params.authors = params.authors.split(",");
    }

    if (params.keywords) {
      params.keywords = params.keywords.split(",");
    }

    await store.dispatch("loadSearchDashboard", { params, req, res });
  },
  head() {
    const { title, siteName, domain, partner } = this;

    const displayTitle = getPageTitle(siteName);
    const pageTitle = title + " | " + displayTitle;

    const canonicalUrl = toCanonicalUrl(`search-dashboard`, partner);

    return {
      title: pageTitle,

      meta: [
        {
          hid: "description",
          name: "description",
          content: `Search ${siteName} to find early research outputs in ${domain}.`,
        },
      ],
      link: [{ rel: "canonical", href: canonicalUrl }],
    };
  },
  computed: {
    ...mapState({
      layout: (state) => state.theme.layout,
      subjectTypes: (state) => state.referenceData.subjectTypes,
      categoryTypes: (state) => state.referenceData.categoryTypes,
      searchRequest: (state) => state.searchRequest,
      searchResult: (state) => state.searchResult,
      siteName: (state) => getSiteName(state.theme),
      domain: (state) => state.theme.domain,
      displayedCount: (state) => {
        if (state?.searchResult?.results) {
          return state.searchResult.results.length;
        }
      },
      totalCount: (state) => {
        if (state.searchResult) {
          return state.searchResult.totalCount;
        }
        return -1;
      },
    }),
    ...mapFlags([
      "searchDashboardShowEventFacets",
      "showContentTypeSearch",
      "showApsaEducateSearchLink",
    ]),
    partner() {
      return this.$config.partner;
    },
    showSubjectFacets() {
      return this.layout.searchDashboardShowSubjectFacets;
    },
    showCategoryFacets() {
      return this.layout.searchDashboardShowCategoryFacets;
    },
    showPartnerFacets() {
      return this.layout.searchDashboardShowPartnerFacets;
    },
    showPublishedDateFacets() {
      return true;
    },
    searchInputColor() {
      return this.layout.searchDashboardSearchInputColor;
    },
    searchInputBold() {
      return this.layout.searchDashboardSearchInputBold;
    },
    displayIsMobile,
    displayIsTablet,
    displayIsDesktop,
    title() {
      const { searchRequest } = this;

      if (searchRequest.subject !== undefined) {
        const subject = this.subjectTypes.find(
          (item) => item.id === searchRequest.subject
        );

        return "Search " + (subject ? subject.name : "");
      } else if (searchRequest.category !== undefined) {
        const category = this.categoryTypes.find(
          (item) => item.id === searchRequest.category
        );

        return "Search " + (category ? category.name : "");
      }
      return "Search";
    },
    showRefine() {
      return !(this.displayIsMobile && this.showDetails === false);
    },
    selectedSubjectBuckets() {
      return this.searchResult.subjectBuckets.filter((item) =>
        this.searchRequest.subjects.includes(item.key)
      );
    },
    selectedCategoryBuckets() {
      return this.searchResult.categoryBuckets.filter((item) =>
        this.searchRequest.categories.includes(item.key)
      );
    },
    selectedContentBuckets() {
      return this.searchResult.contentBuckets.filter((item) =>
        this.searchRequest.contents.includes(item.key)
      );
    },
    selectedEventBuckets() {
      return this.searchResult.eventBuckets.filter((item) =>
        this.searchRequest.events.includes(item.key)
      );
    },
    selectedPublishedDateBuckets() {
      return this.searchResult.publishedDateBuckets.filter((item) =>
        this.searchRequest.publishedDates.includes(item.key)
      );
    },
    selectedPartnerBuckets() {
      return this.searchResult.partnerBuckets.filter((item) =>
        this.searchRequest.partners.includes(item.key)
      );
    },
    solidHeader() {
      return this.layout.mainBannerSolidHeader;
    },
    selectedFacetNames() {
      return {
        subjects: {
          name: "Subjects",
          items: this.selectedSubjectBuckets.map((item) => item.label),
        },
        contents: {
          name: "Contents",
          items: this.selectedContentBuckets.map((item) => item.label),
        },
        categories: {
          name: "Categories",
          items: this.selectedCategoryBuckets.map((item) => item.label),
        },
        partners: {
          name: "Partners",
          items: this.selectedPartnerBuckets.map((item) => item.label),
        },
        events: {
          name: "Events",
          items: this.selectedEventBuckets.map((item) => item.label),
        },
        publishedDates: {
          name: "Published Dates",
          items: this.selectedPublishedDateBuckets.map((item) => item.label),
        },
        authors: { name: "Authors", items: this.searchRequest.authors },
        keywords: { name: "Keywords", items: this.searchRequest.keywords },
      };
    },
  },
  watch: {
    searchResult() {
      this.gettingFirst = false;
    },
    totalCount() {
      this.onShowMore();
    },
    displayedCount() {
      this.gettingMore = false;
      this.gettingFirst = false;
    },
    selectedSort() {
      const sortHandled = this.sortOptions.find(
        (opts) => opts.key === this.searchRequest.sortBy
      );

      if (sortHandled) {
        this.selectedSort = sortHandled;
      } else {
        this.selectedSort = this.sortOptions[0];
      }
    },
  },
  watchQuery: [
    "text",
    "category",
    "subject",
    "partners",
    "content",
    "events",
    "publishedDates",
    "subjects",
    "categories",
    "contents",
    "authors",
    "keywords",
    "sortBy",
    "orp-no-events",
  ],
  created() {
    if (this.searchRequest.sortBy) {
      this.selectedSort =
        this.sortOptions.find((opts) => opts.key === this.searchRequest.sortBy)
          ?.key || this.sortOptions[0].key;
    } else {
      this.selectedSort = this.sortOptions[0].key;
    }
  },
  methods: {
    async onShowMore() {
      if (
        this.gettingMore === true ||
        this.displayedCount === this.totalCount
      ) {
        return;
      }

      this.gettingMore = true;

      const params = {
        ...this.searchRequest,
        skip: this.searchResult.results.length,
      };

      await this.$store.dispatch("loadSearchMoreMatches", { params });

      this.gettingMore = false;
    },
    onUpdateSearchText(text) {
      this.$store.dispatch("modifySearch", { text });
    },
    handleContentChanged({ id, value }) {
      let contents = this.searchRequest.contents
        ? [...this.searchRequest.contents]
        : [];

      if (value === true) {
        contents.push(id);
      } else {
        contents = contents.filter((contentId) => contentId !== id);
      }

      this.$store.dispatch("modifySearch", { contents }).then(() => {
        this.onSearch();
      });
    },
    handleSubjectChanged({ id, value }) {
      let subjects = this.searchRequest.subjects
        ? [...this.searchRequest.subjects]
        : [];

      if (value === true) {
        subjects.push(id);
      } else {
        subjects = subjects.filter((subjectId) => subjectId !== id);
      }

      this.$store.dispatch("modifySearch", { subjects }).then(() => {
        this.onSearch();
      });
    },
    handleEventChanged({ id, value }) {
      let events = this.searchRequest.events
        ? [...this.searchRequest.events]
        : [];

      if (value === true) {
        events.push(id);
      } else {
        events = events.filter((eventId) => eventId !== id);
      }

      this.$store.dispatch("modifySearch", { events }).then(() => {
        this.onSearch();
      });
    },
    handleCategoryChanged({ id, value }) {
      let categories = this.searchRequest.categories
        ? [...this.searchRequest.categories]
        : [];

      if (value === true) {
        categories.push(id);
      } else {
        categories = categories.filter((categoryId) => categoryId !== id);
      }

      this.$store.dispatch("modifySearch", { categories }).then(() => {
        this.onSearch();
      });
    },
    handlePublishedDateChanged({ id, value }) {
      let publishedDates = this.searchRequest.publishedDates
        ? [...this.searchRequest.publishedDates]
        : [];

      if (value === true) {
        publishedDates.push(id);
      } else {
        publishedDates = publishedDates.filter(
          (publishedDateId) => publishedDateId !== id
        );
      }

      this.$store.dispatch("modifySearch", { publishedDates }).then(() => {
        this.onSearch();
      });
    },
    handlePartnerChanged({ id, value }) {
      let partners = this.searchRequest.partners
        ? [...this.searchRequest.partners]
        : [];

      if (value === true) {
        partners.push(id);
      } else {
        partners = partners.filter((partnerId) => partnerId !== id);
      }

      this.$store.dispatch("modifySearch", { partners }).then(() => {
        this.onSearch();
      });
    },
    handleAuthorsChanged(authors) {
      this.$store.dispatch("modifySearch", { authors }).then(() => {
        this.onSearch();
      });
    },
    handleSortChanged(sortBy) {
      const { searchRequest } = this;

      if (sortBy !== searchRequest.sortBy) {
        this.sortChanged = true;
        this.$store.dispatch("modifySearch", { sortBy }).then(() => {
          this.onSearch();
        });
      }
    },
    handleKeywordsChanged(keywords) {
      this.$store.dispatch("modifySearch", { keywords }).then(() => {
        this.onSearch();
      });
    },
    onSearch() {
      const { searchRequest } = this;

      const oldPath = decodeURIComponent(this.$router.currentRoute.fullPath);
      const sortBy = searchRequest.sortBy
        ? "sortBy=" + searchRequest.sortBy
        : undefined;

      let searchText =
        typeof searchRequest.text === "string" ? searchRequest.text.trim() : "";

      let contents;
      let subjects;
      let categories;
      let partners;
      let events;
      let publishedDates;
      let authors;
      let keywords;
      let query = "";
      let newPath = "";

      this.gettingFirst = true;

      if (searchText !== "") {
        searchText = `text=${encodeURIComponent(searchText)}`;
      } else {
        searchText = undefined;
      }

      if (searchRequest?.subjects?.length > 0) {
        subjects = "subjects=" + searchRequest.subjects.join(",");
      }

      if (searchRequest?.categories?.length > 0) {
        categories = "categories=" + searchRequest.categories.join(",");
      }

      if (searchRequest?.partners?.length > 0) {
        partners = "partners=" + searchRequest.partners.join(",");
      }

      if (searchRequest?.contents?.length > 0) {
        contents = "contents=" + searchRequest.contents.join(",");
      }

      if (searchRequest?.events?.length > 0) {
        events = "events=" + searchRequest.events.join(",");
      }

      if (searchRequest?.publishedDates?.length > 0) {
        publishedDates =
          "publishedDates=" + searchRequest.publishedDates.join(",");
      }

      if (searchRequest?.authors?.length > 0) {
        const authorsString = searchRequest.authors
          .map((item) => encodeURIComponent(item))
          .join(",");

        authors = "authors=" + authorsString;
      }

      if (searchRequest?.keywords?.length > 0) {
        const keywordsString = searchRequest.keywords
          .map((item) => encodeURIComponent(item))
          .join(",");

        keywords = "keywords=" + keywordsString;
      }

      query = [
        searchText,
        contents,
        events,
        publishedDates,
        subjects,
        categories,
        partners,
        authors,
        keywords,
        sortBy,
      ]
        .filter((item) => item)
        .join("&");

      newPath = `/engage/${this.partner}/search-dashboard${
        query ? "?" + query : ""
      }`;

      if (oldPath === newPath) {
        setTimeout(() => {
          this.gettingFirst = false;
        }, 500);
      } else {
        this.$router.push({
          path: newPath,
        });
      }
    },
    onToggleDisplay() {
      this.showDetails = !this.showDetails;
    },
  },
};
</script>
